// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import environment from "../../config/environment";
import { getOrCreateTabId } from "../browserTab";

/**
 * Sets Amplitude user properties, given the required parameters.
 * @param {amplitude} amplitude - The Amplitude instance used to set user properties.
 * @param {object} userProperties - An object containing user properties.
 * @param {string} userProperties.sessionId - The session ID of the user.
 * @param {string} userProperties.userId - The MongoDB ID of the user.
 * @param {string} userProperties.userSQLId - The SQL ID of the user.
 * @param {string} userProperties.firstName - The first name of the user.
 * @param {string} userProperties.lastName - The last name of the user.
 * @param {string} userProperties.email - The email address of the user.
 * @param {string} userProperties.client - The name of the client.
 * @param {string} userProperties.permission - The permission level of the user.
 * 
 * @returns {Promise<void>} - A promise that resolves when the user properties have been set.
 */
async function setAmplitudeUserProperties(amplitude, { sessionId, userId, userSQLId, firstName, lastName, email, client, permission }) {
    const getDeviceId = localStorage.getItem("amplitudeDeviceId");
    if (getDeviceId) {
        amplitude.setDeviceId(getDeviceId);
    }
    const timestamp = Date.now();
    amplitude.setSessionId(timestamp); // throws error if any other string passed
    amplitude.setUserId(userId);
    const identify = new amplitude.Identify()
    identify.set('Permission', permission);
    identify.set('FirstName', firstName);
    identify.set('LastName', lastName);
    identify.set('Email', email);
    identify.set('UserMongoId', userId);
    identify.set('UserSQLId', userSQLId);
    identify.set('Client', permission === "HWAI_ADMIN" ? 'All' : client);
    identify.set('MongoSessionId', sessionId);
    identify.set('Instance', environment);
    
    await amplitude.identify(identify).promise;
}

/**
 * Re-identifies and updates the Amplitude user properties.
 *
 * @param {object} amplitude - The Amplitude instance used to set user properties.
 * @param {object} userProperties - An object containing user properties.
 * @param {string} userProperties.firstName - The first name of the user.
 * @param {string} userProperties.lastName - The last name of the user.
 * @param {string} userProperties.email - The email address of the user.
 * @param {string} userProperties.userId - The MongoDB ID of the user.
 * @param {string} userProperties.userSQLId - The SQL ID of the user.
 * @param {string} userProperties.client - The name of the client.
 * @param {string} userProperties.permission - The permission level of the user.
 *
 * This function retrieves the device ID from local storage and sets it in the Amplitude instance
 * if available. It then sets the user ID and identifies user properties such as permission,
 * first name, last name, email, user IDs, client, and environment instance.
 *
 * @returns {Promise<void>} - A promise that resolves when the user properties have been updated.
 */
async function reIdentifyAmplitudeUserProperties(amplitude, { firstName, lastName, email, userId, userSQLId, client, permission }) {
    // console.log("reIdentify user properties:", firstName, lastName, email, userId, userSQLId, client, permission);
    const getDeviceId = localStorage.getItem("amplitudeDeviceId");
    if (getDeviceId) {
        amplitude.setDeviceId(getDeviceId);
    }
    amplitude.setUserId(userId);

    const identify = new amplitude.Identify()
    identify.set('Permission', permission);
    identify.set('FirstName', firstName);
    identify.set('LastName', lastName);
    identify.set('Email', email);
    identify.set('UserMongoId', userId);
    identify.set('UserSQLId', userSQLId);
    identify.set('Client', permission === "HWAI_ADMIN" ? 'All' : client);
    identify.set('Instance', environment);
    
    await amplitude.identify(identify).promise;
}

/**
 * Logs an Amplitude event with a given name and properties.
 *
 * @param {object} amplitude - The Amplitude instance used to log the event.
 * @param {string} eventName - The name of the event to log.
 * @param {object} eventProperties - The event properties to log.
 * @param {string} [overrideTabId] - An optional tab ID to override the default tab ID.
 *
 * The default tab ID is set to the value of window.name if it exists, otherwise it is set to a
 * unique tab ID generated by getOrCreateTabId(). If both of these are null, the tab ID is set
 * to null.
 *
 * @returns {Promise<void>} - A promise that resolves when the event has been logged.
 */
async function logAmplitudeEvent(amplitude, eventName, eventProperties, overrideTabId = undefined) {
    await amplitude.track(eventName, { ...eventProperties, tabId: overrideTabId ?? window.name ?? getOrCreateTabId() ?? null }).promise;
}

const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 1,
});

/**
 * Enables Amplitude session replay tracking.
 *
 * @param {AmplitudeClient} amplitude - the amplitude client instance
 *
 * @returns {Promise<void>} - A promise that resolves when the plugin has been added to Amplitude.
 */
async function startSessionReplayTracking(amplitude) {
    await amplitude.add(sessionReplayTracking).promise
}

export { setAmplitudeUserProperties, logAmplitudeEvent, sessionReplayTracking, reIdentifyAmplitudeUserProperties, startSessionReplayTracking };